import "./styles.scss";

import { Col, Row } from "@amzn/stencil-react-components/layout";
import { GridContainer, GridItem, View } from "@amzn/stencil-react-components/layout";
import { Link } from "@amzn/stencil-react-components/link";
import { Select } from "@amzn/stencil-react-components/select";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Label } from "@amzn/stencil-react-components/text";
import { Text } from "@amzn/stencil-react-components/text";
import { TaxonomyIdItem } from "@amzn/taxonomy-lib";
import { Input } from "@amzn/uxm-stencil-react-components/lib/src/stencil/input";
import { Select as UxmSelect } from "@amzn/uxm-stencil-react-components/lib/src/stencil/select";
import { trackCustomEvent } from "@amzn/uxm-stencil-react-components/lib/src/uxm/custom-event";
import { AiForMeURLQueryStrings } from "common/types";
import { getString } from "common/uistringlabels/uiStringUtils";
import { config, PERMISSIONS } from "config";
import { useLists } from "hooks/ListsContext";
import { useSearchExperience } from "hooks";
import { useAIForMeData } from "hooks/useAIForMeData";
import { AIForMeTool, sortingOptions, SortOption } from "lib/models/aiforme-models";
import { AmazonianInformation } from "lib/models/oui-models";
import { CatalogicalService, UserPermissions } from "lib/services";
import { FeedbackModelRecord, GetFeedbacksResponse, GetItemsResponse } from "lib/services/catalogical-models";
import { CSRFService } from "lib/services/csrf-service";
import { CatalogicalSearchRequestContent } from "lib/services/shop-search-service";
import { UserInformation } from "lib/services/user-information-service";
import { debounce } from "lodash";
import React, { FC, ReactNode, useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import jobFamiliesMapping from "../../../configuration/AIForMe/jobFamiliesMapping.json";
import { BuilderHubBanner } from "../MessageBanners";
import { AiForMeItem } from "./AiForMeItem";
import { MultiSelect } from "./MultiSelect";
import { AiForMeSearchListState } from "./useAiForMeSearchList";

const CATEGORY_SOFT_DEV = "Software development";
const ALL_JOB_FAMILY_CATEGORY = "All";

export enum CategoriesWithTooltip {
  SOFTWARE_DEV = "Software development",
  LEGAL_MANAGEMENT = "Legal Management",
  LEGAL_PUBLIC_POLICY = "Legal & Public Policy",
}

let voteTO: NodeJS.Timeout | undefined;

const SEND_METRICS = true;

const HANDLER_DEBOUNCE_TIMEOUT = 300;
const PAGE_SIZE = 30;

export interface AiForMeSearchListProps {
  inputs: AiForMeSearchListState;
  updateInputs: (key: keyof AiForMeSearchListState, value: any) => void;
}

/**
 * Component to display search and results of AiForMe
 * @param props
 * @returns
 */
export const AiForMeSearchList: FC<AiForMeSearchListProps> = (props: AiForMeSearchListProps) => {
  const { categories, jobFamilies, sortingPriority, dataClassifications, loadingAIForMe } = useAIForMeData(true);
  const client = new CatalogicalService<AIForMeTool>("aiforme");
  const csrfService = new CSRFService();
  const location = useLocation();
  // We know for sure the AmazonianInformation was obtained when AuthLoading component was executed
  const userInfo = UserInformation.current.getCachedAmazonianInformation() as AmazonianInformation;
  // Feature flag to enable semantic search
  const useSemanticSearch = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_AIFORME_SEMANTIC_SEARCH);

  const { items, isLoading, isLoadingMore, hasMoreItems, searchCatalogItems, searchNext, totalCount, nextToken } =
    useSearchExperience<AIForMeTool>("aiforme");

  const { inputs, updateInputs } = props;
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { lists, getLists } = useLists();
  const toggleLists = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_LISTS);
  useEffect(() => {
    if (toggleLists) {
      getLists({ allowedDomains: ["aiforme"] });
    }
  }, [getLists]);

  // Update banner visibility when categories change
  useEffect(() => {
    updateInputs(
      "showBuilderHubBanner",
      userInfo?.jobInfo.isTechnicalRole || (inputs.selectedCategories?.includes(CATEGORY_SOFT_DEV) ?? false)
    );
  }, [inputs.selectedCategories, userInfo?.jobInfo.isTechnicalRole]);
  const observer = useRef<IntersectionObserver | null>(null);
  const lastElementRef = useRef<HTMLDivElement | null>(null);

  const trackUxmGetItems = (params: {
    searchText?: string;
    filters: Record<string, any>;
    sortBy?: string;
    sortDirection?: string;
    pageSize: number;
    pageIndex: number;
  }) => {
    const trackerId = config.analytics.uxmAppId;
    const event = {
      ...params,
    };

    trackCustomEvent(event, "get-ai-tools", trackerId);
  };

  const trackUxmSelectChange = (option: any, touchpoint: string) => {
    const trackerId = config.analytics.uxmAppId;
    const event = {
      option,
    };

    trackCustomEvent(event, touchpoint, trackerId);
  };

  const parseArrayParam = (param: string | null, validOptions: string[]) =>
    param?.split(",").filter((item) => validOptions.includes(item)) || [];

  const getURLParams = () => {
    const params = new URLSearchParams(location.search);

    const sortParam = params.get(AiForMeURLQueryStrings.SORT);
    const categoriesParam = params.get(AiForMeURLQueryStrings.CATEGORIES);
    const jobFamilyParam = params.get(AiForMeURLQueryStrings.JOB_FAMILY);
    const dataClassificationParam = params.get(AiForMeURLQueryStrings.DATA_CLASSIFICATION);

    return { sortParam, categoriesParam, jobFamilyParam, dataClassificationParam };
  };

  // Initialize filters and sorting from URL on component mount
  useEffect(() => {
    if (loadingAIForMe) return;

    const { sortParam, categoriesParam, jobFamilyParam, dataClassificationParam } = getURLParams();

    // Set Job Families from URL params, if not set, then user's job family will be used
    if (jobFamilyParam) {
      const validJobFamiliesArray = parseArrayParam(jobFamilyParam, jobFamilies);
      updateInputs("selectedJobFamilies", validJobFamiliesArray);
    } else if (jobFamilyParam === "") {
      updateInputs("selectedJobFamilies", []);
    } else {
      const jobFamily = (jobFamiliesMapping as { [key: string]: string })[userInfo?.jobInfo?.jobFamilyName];
      updateInputs("selectedJobFamilies", jobFamily ? [jobFamily] : []);
    }

    // Set data classification from URL params
    if (dataClassificationParam) {
      const validDataClassificationsArray = parseArrayParam(dataClassificationParam, dataClassifications);
      updateInputs("selectedDataClassification", validDataClassificationsArray);
    }

    // Set categories from URL params
    if (categoriesParam) {
      const validCategoriesArray = parseArrayParam(categoriesParam, categories);
      updateInputs("selectedCategories", validCategoriesArray);
    }

    // Set sortBy from URL params
    if (sortParam) {
      const newSort = sortingOptions.findIndex((option) => option.label === sortParam);

      if (newSort !== -1) {
        updateInputs("sortBy", sortingOptions[newSort]);
      }
    }

    // Mark as initialized after URL params are processed
    updateInputs("isInitialized", true);
  }, [loadingAIForMe]);

  const updateUrl = useCallback(
    (newParams: Record<string, any>) => {
      const newUrlParams = new URLSearchParams();
      if (newParams.sortBy) {
        newUrlParams.set(AiForMeURLQueryStrings.SORT, newParams.sortBy);
      }

      if (newParams.searchText) {
        newUrlParams.set(AiForMeURLQueryStrings.QUERY, newParams.searchText);
      }

      if (newParams.selectedJobFamilies) {
        newUrlParams.set(AiForMeURLQueryStrings.JOB_FAMILY, newParams.selectedJobFamilies);
      }

      if (newParams.selectedCategories && newParams.selectedCategories.length > 0) {
        newUrlParams.set(AiForMeURLQueryStrings.CATEGORIES, newParams.selectedCategories);
      }

      if (newParams.selectedDataClassification && newParams.selectedDataClassification.length > 0) {
        newUrlParams.set(AiForMeURLQueryStrings.DATA_CLASSIFICATION, newParams.selectedDataClassification);
      }

      const newUrl = `${location.pathname}?${newUrlParams.toString()}`;
      window.history.replaceState({}, "", newUrl);
    },
    [location.pathname, location.search]
  );

  const debouncedUpdateUrl = useCallback(
    debounce((params) => {
      updateUrl(params);
    }, HANDLER_DEBOUNCE_TIMEOUT),
    [updateUrl]
  );

  const handleSortChange = useCallback((value?: SortOption) => {
    if (value) {
      const newSort = sortingOptions.findIndex((option) => option.label === value.label);
      updateInputs("sortBy", sortingOptions[newSort]);
    }
  }, []);

  const handleJobFamilyChange = useCallback((jobFamily?: string[]) => {
    trackUxmSelectChange(jobFamily, "aiforme-select-job-families");
    updateInputs("selectedJobFamilies", jobFamily);
  }, []);

  const handleDataClassificationChange = useCallback((dataClassification?: string[]) => {
    trackUxmSelectChange(dataClassification, "aiforme-select-data-classification");
    updateInputs("selectedDataClassification", dataClassification);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleCategoriesChange = useCallback((categories: string[]) => {
    updateInputs("selectedCategories", categories);
  }, []);

  async function doGetItems(loadMore: boolean = false): Promise<void> {
    const filters: Record<string, any> = {
      status: ["APPROVED", "UPDATED"],
    };
    if (inputs.selectedCategories && inputs.selectedCategories.length > 0) {
      filters["categories"] = inputs.selectedCategories;
    }
    if (inputs.selectedJobFamilies && inputs.selectedJobFamilies.length > 0) {
      filters["jobFamilies"] = [...inputs.selectedJobFamilies, ALL_JOB_FAMILY_CATEGORY];
    }

    if (inputs.selectedDataClassification && inputs.selectedDataClassification.length > 0) {
      filters["dataClassification"] = inputs.selectedDataClassification;
    }

    const currentPageIndex = nextToken / PAGE_SIZE;

    try {
      console.info(`Querying page ${currentPageIndex}`);
      trackUxmGetItems({
        searchText: inputs.searchText,
        filters,
        sortBy: inputs.sortBy?.field.join(","),
        sortDirection: inputs.sortBy?.order?.join(","),
        pageSize: PAGE_SIZE,
        pageIndex: currentPageIndex,
      });

      const nextRequest: CatalogicalSearchRequestContent = {
        searchQuery: inputs.searchText,
        useSemanticSearch,
        caseInsensitive: true,
        sortBy: inputs.sortBy?.field.join(","),
        sortDirection: inputs.sortBy?.order?.join(","),
        filter: JSON.stringify(filters),
        limit: PAGE_SIZE,
        offset: loadMore && hasMoreItems ? nextToken : 0,
        vectorField: "embeddings", // TODO: Move to Search service domain specific config
      };

      let response: GetItemsResponse<AIForMeTool> | undefined;
      if (loadMore && hasMoreItems) {
        response = await searchNext(nextRequest);
      } else {
        response = await searchCatalogItems(nextRequest, SEND_METRICS);
      }

      /* istanbul ignore next */
      if (!response) {
        const err = { name: "No response", message: "No response getting the items" };
        updateInputs("error", err);
        return;
      }

      // Good response, set data and pagination
      updateInputs("error", undefined);
    } catch (
      err
      /* istanbul ignore next */
    ) {
      updateInputs("error", err as Error);
    }
  }

  // Function to load more data when scrolling to the bottom
  /* istanbul ignore next */
  const loadMoreItems = useCallback(() => {
    if (!isLoading && !isLoadingMore && hasMoreItems) {
      console.info("Loading next page...");
      doGetItems(true);
    }
  }, [isLoading, isLoadingMore, hasMoreItems]);

  // Setup intersection observer for infinite scrolling
  useEffect(() => {
    if (isLoading || isLoadingMore) return;

    if (observer.current) observer.current.disconnect();

    /* istanbul ignore next */
    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreItems) {
          loadMoreItems();
        }
      },
      { threshold: 0.5 }
    );

    if (lastElementRef.current) {
      observer.current.observe(lastElementRef.current);
    }

    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, [loadMoreItems, isLoading, isLoadingMore, hasMoreItems, items]);

  useEffect(() => {
    if (inputs.isInitialized) {
      debouncedUpdateUrl({
        searchText: inputs.searchText,
        selectedCategories: inputs.selectedCategories,
        selectedJobFamilies: inputs.selectedJobFamilies,
        selectedDataClassification: inputs.selectedDataClassification,
        sortBy: inputs.sortBy?.label,
      });

      // Debounce API calls to prevent multiple requests
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        doGetItems(false);
      }, 100); // Short debounce to batch rapid state changes
    }
  }, [
    inputs.searchText,
    inputs.selectedCategories,
    inputs.selectedJobFamilies,
    inputs.selectedDataClassification,
    inputs.sortBy,
    inputs.isInitialized,
  ]);

  /**
   * Gets the feedback the user has submitted from backend
   */
  const fetchUserFeedbacks = async (): Promise<void> => {
    try {
      const myFeedbackResponse: GetFeedbacksResponse | undefined = await client.getMyFeedbacks();

      if (myFeedbackResponse && myFeedbackResponse.data) {
        // Create a map of taxonomyId to feedback
        const feedbackMap: Record<string, FeedbackModelRecord> = {};
        myFeedbackResponse.data.forEach((feedback) => {
          if (feedback && feedback.taxonomyId) {
            feedbackMap[feedback.taxonomyId] = feedback;
          }
        });

        // Update state with the feedback data
        updateInputs("userFeedbacks", feedbackMap);
      }
    } catch (error) {
      console.error("Error fetching user feedbacks:", error);
    }
  };

  useEffect(() => {
    // Load feedback data every time the component mounts
    fetchUserFeedbacks();
  }, []);

  /* istanbul ignore next */
  /**
   * Handles submitting a feedback vote. Uses a timeout to prevent multiple submits
   * @param taxId The taxonomy id
   * @param vote The vote value
   */
  async function voteHandler(taxId: TaxonomyIdItem, vote: number): Promise<boolean> {
    const token = await csrfService.getToken();

    const voteHandlerPromise = new Promise<boolean>((resolve, reject) => {
      /* istanbul ignore next */
      if (voteTO) {
        clearTimeout(voteTO);
        reject("debounce");
      }

      voteTO = setTimeout(async () => {
        client
          .postFeedback(
            taxId,
            {
              rating: vote,
            },
            token
          )
          .then((response: FeedbackModelRecord) => {
            // Update the userFeedbacks state with the new vote
            updateInputs("userFeedbacks", {
              ...inputs.userFeedbacks,
              [taxId.toString()]: response,
            });
            resolve(true);
          })
          .catch((reason) => {
            console.error(reason);
            resolve(false);
          })
          .finally(() => {
            voteTO = undefined;
          });
      }, HANDLER_DEBOUNCE_TIMEOUT);
    });
    return voteHandlerPromise;
  }

  let children: React.JSX.Element[] | undefined;

  if (items) {
    children = items.map((item: AIForMeTool, index: number) => {
      // Get the user's vote for this item if it exists
      const userVote = inputs.userFeedbacks[item.taxonomyId];

      return (
        <AiForMeItem
          key={item.taxonomyId}
          item={item}
          index={index}
          voteHandler={voteHandler}
          sortingPriorityTag={item.sortingPriority ? sortingPriority[item.sortingPriority] : undefined}
          client={client}
          userVote={userVote}
          lists={lists}
        />
      );
    });
  }

  //If more tooltips are needed they can be added to this array
  const showToolTip: (ReactNode | undefined)[] = categories.map((cat) => {
    switch (cat) {
      case CategoriesWithTooltip.SOFTWARE_DEV:
        return (
          <Text>
            {getString("aiForMe.builderHubBanner.message")}{" "}
            <Link href={getString("aiForMe.builderHubBanner.linkUrl")} className="softDevToolTip" target="_blank">
              {getString("aiForMe.builderHubBanner.linkText")}
            </Link>
          </Text>
        );
      case CategoriesWithTooltip.LEGAL_MANAGEMENT:
      case CategoriesWithTooltip.LEGAL_PUBLIC_POLICY:
        return (
          <Text>
            {getString("aiForMe.legal&PublicPolicyTooltip.messageStart")}{" "}
            <Link
              href={getString("aiForMe.legal&PublicPolicyTooltip.linkUrl")}
              className="legalToolTip"
              target="_blank"
            >
              {getString("aiForMe.legal&PublicPolicyTooltip.linkText")}
            </Link>{" "}
            {getString("aiForMe.legal&PublicPolicyTooltip.messageEnd")}
          </Text>
        );
      default:
        return undefined;
    }
  });

  return (
    <GridContainer>
      <GridItem lg={3} xl={2} md={8} sm={4}>
        <Col rowGap={20} minWidth="100%" data-testid="sidebar" id="search-sidebar">
          <View minWidth="100%">
            <Label fontSize="T300" fontWeight="bold" htmlFor="jobFamilies">
              {getString("aiForMe.jobFamily")}
            </Label>
            {loadingAIForMe ? (
              <Spinner size={SpinnerSize.Small}></Spinner>
            ) : (
              <Select
                id="jobFamilies"
                options={jobFamilies}
                isMulti={true}
                value={inputs.selectedJobFamilies}
                data-testid="sel_jobFamilies"
                onChange={handleJobFamilyChange}
              />
            )}
          </View>
          <View minWidth="100%">
            <Label fontSize="T300" fontWeight="bold" htmlFor="dataClassification">
              {getString("aiForMe.dataClassification")}
            </Label>
            {loadingAIForMe ? (
              <Spinner size={SpinnerSize.Small}></Spinner>
            ) : (
              <Select
                id="dataClassification"
                options={dataClassifications}
                isMulti={true}
                value={inputs.selectedDataClassification}
                data-testid="sel_dataClassification"
                onChange={handleDataClassificationChange}
              />
            )}
          </View>
          <View minWidth="100%">
            <Label fontSize="T300" fontWeight="bold" htmlFor="categories">
              {getString("aiForMe.categories")}
            </Label>
            {(loadingAIForMe || !inputs.isInitialized) && <Spinner size={SpinnerSize.Small}></Spinner>}
            {!loadingAIForMe && (
              <MultiSelect
                id="categories"
                columns={1}
                options={categories}
                value={inputs.selectedCategories}
                data-testid="sel_categories"
                touchpoint="categories-filter"
                onChange={handleCategoriesChange}
                showToolTip={showToolTip}
              />
            )}
          </View>
        </Col>
      </GridItem>
      <GridItem lg={9} xl={10} md={8} sm={4} data-testid="results">
        <Col minWidth="100%" rowGap={32}>
          <Row alignItems="center" justifyContent="space-between" gridGap={32} padding={0}>
            <Text fontSize="T400" fontWeight="bold" data-testid="results-header">
              {getString("aiForMe.aiTools")} {isLoading ? "" : `(${totalCount})`}
            </Text>
            <Row alignItems="center" gridGap={5} padding={0}>
              <Text>Sort&nbsp;by:</Text>
              <UxmSelect
                options={sortingOptions}
                isMulti={false}
                onChange={handleSortChange}
                value={inputs.sortBy}
                valueAccessor={(option: SortOption) => option.label}
                data-testid="sel_sortBy"
                renderOption={(option: SortOption) => {
                  return <Text>{option.label}</Text>;
                }}
                touchpoint="sort-options-select"
              />
            </Row>
          </Row>

          {/* BuilderHub Banner */}

          {inputs.showBuilderHubBanner && (
            <div className="builderhub-link">
              <BuilderHubBanner />
            </div>
          )}

          {isLoading && <Spinner size={SpinnerSize.Medium} />}

          {/* Display results */}
          {!isLoading && children && (
            <GridContainer>
              {children}
              <GridItem lg={9} xl={9} md={8} sm={4}>
                <div ref={lastElementRef} style={{ height: "20px", width: "100%" }}></div>
                {isLoadingMore && (
                  <Row justifyContent="center" padding={16}>
                    <Spinner size={SpinnerSize.Small} />
                  </Row>
                )}
                {!hasMoreItems && items && items.length > 0 && (
                  <Row justifyContent="center" padding={16}>
                    <Text fontSize="T200" color="gray">
                      {getString("aiForMe.noMoreItems")}
                    </Text>
                  </Row>
                )}
              </GridItem>
            </GridContainer>
          )}
        </Col>
      </GridItem>
    </GridContainer>
  );
};
